import { PermissionSetType, PermissionType } from '@app-store'
import { AssignPermissionSidebarProps } from '../../types/permissionAssignment.types'
import { PERMISSION_ASSIGNMENT } from '../../utils/constants'
import { Checkbox, SearchBar, Tooltip } from '@ui-kit'
import { useEffect, useRef, useState } from 'react'

const AssignPermissionSidebar = ({
  checkedPermissionSets,
  handleCheck,
  allPermissionSets,
}: AssignPermissionSidebarProps) => {
  const [permissionSets, setPermissionSets] = useState<PermissionSetType[]>([])
  const handleSearchPermissions = (value: string) => {
    const search_value = value?.trim()
    if (search_value?.length >= 3) {
      const search_results = allPermissionSets?.filter((item) =>
        item.permission_set_name
          .toLocaleLowerCase()
          ?.includes(search_value.toLocaleLowerCase())
      )
      setPermissionSets(search_results)
    }
    if (!search_value.length) {
      setPermissionSets(allPermissionSets)
    }
  }

  useEffect(() => {
    setPermissionSets(allPermissionSets)
  }, [allPermissionSets])

  return (
    <div className="flex flex-col gap-2.5 p-2.5 border rounded border-border-100 overflow-auto">
      <div className=" font-primary font-medium text-xs w-full bg-secondary-50 p-1 rounded-sm">
        {PERMISSION_ASSIGNMENT.SELECT_PERMISSIONS}
      </div>
      <div className="flex flex-1 min-h-0 flex-col border border-border-100">
        <SearchBar
          width="w-full"
          height="h-10"
          onSearch={(value) => {
            handleSearchPermissions(value)
          }}
          showFunnel={false}
          dataQaId="permission-set-search-value"
        />
        {permissionSets?.length ? (
          <div className="flex flex-1 min-h-0 min-w-0 flex-col overflow-auto p-1.5 gap-1.5">
            {permissionSets.map((item: PermissionSetType, key) => {
              return (
                <div
                  className={`flex items-start shrink-0 p-2 gap-1 border-l-2 ${
                    checkedPermissionSets[item.permission_set_id]
                      ? 'border-secondary-500'
                      : 'border-border-500'
                  } shadow-custom rounded overflow-hidden`}
                  key={`${item.permission_set_id}_${key}`}
                  onClick={() => handleCheck(item)}
                >
                  <div className="py-1">
                    <Checkbox
                      label=""
                      name={item.permission_set_name}
                      checked={
                        !!checkedPermissionSets[item.permission_set_id] || false
                      }
                      readOnly={true}
                      dataQaId={`permission-set-name-checkbox-${item.permission_set_name}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0 flex-col gap-1">
                    <div className="font-primary font-medium text-xs/5">
                      {item.permission_set_name}
                    </div>
                    <PermissionRenderer permissionsList={item.permissions} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const PermissionRenderer = ({
  permissionsList,
}: {
  permissionsList: PermissionType[]
}) => {
  const [visibilityNumber, setVisibilityNumber] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const expandRef = useRef<HTMLSpanElement>(null)
  const checkSpansVisibility = () => {
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      const spans = containerRef.current.querySelectorAll(
        'span.permission-span'
      )
      let count = 0
      let lastSpan: DOMRect | null = null
      Array.from(spans).forEach((span) => {
        const spanRect = span.getBoundingClientRect()
        lastSpan = spanRect
        const fullyVisible =
          spanRect.top >= containerRect.top &&
          spanRect.left >= containerRect.left &&
          spanRect.bottom <= containerRect.bottom &&
          spanRect.right <= containerRect.right - 32
        if (!fullyVisible) count++
      })
      if (
        count === 1 &&
        lastSpan &&
        (lastSpan as DOMRect).right <= containerRect.right
      )
        setVisibilityNumber(0)
      else setVisibilityNumber(count)
    }
  }
  useEffect(() => {
    checkSpansVisibility()
    window.addEventListener('resize', checkSpansVisibility)
    window.addEventListener('scroll', checkSpansVisibility)
    return () => {
      window.removeEventListener('resize', checkSpansVisibility)
      window.removeEventListener('scroll', checkSpansVisibility)
    }
  }, [])
  return (
    <div
      ref={containerRef}
      className={`text-xs/5 font-primary font-regular truncate relative${
        visibilityNumber ? ' pr-8' : ''
      }`}
    >
      {permissionsList.map((permission, index) => {
        if (index === 0)
          return (
            <span className="permission-span" key={permission.permission_id}>
              {permission.permission_name}
            </span>
          )
        else
          return (
            <span className="permission-span" key={permission.permission_id}>
              , {permission.permission_name}
            </span>
          )
      })}
      {visibilityNumber ? (
        <span
          ref={expandRef}
          className="absolute right-0 bg-secondary-25 border-l-[1px] border-l-border-50 px-2 rounded"
        >
          <Tooltip content={<TooltipComponent permissions={permissionsList} />}>
            +{visibilityNumber}
          </Tooltip>
        </span>
      ) : null}
    </div>
  )
}

const TooltipComponent = ({
  permissions,
}: {
  permissions: PermissionType[]
}) => {
  const permissionArray: PermissionType[][] = []
  let curr: PermissionType[] = []
  permissions.forEach((per) => {
    curr.push(per)
    if (curr.length === 15) {
      permissionArray.push(curr)
      curr = []
    }
  })
  if (curr.length > 0) permissionArray.push(curr)
  return (
    <div className="flex gap-4">
      {permissionArray.map((perArray, ind) => (
        <div key={`${perArray?.[0]?.permission_name ?? 'group'}-${ind}`}>
          <ul className="list-disc pl-3 pr-1">
            {perArray.map((per, index) => (
              <li
                key={`${per?.permission_name}-${index}`}
                className="text-start p-[0.05rem]"
              >
                {per.permission_name}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

export default AssignPermissionSidebar
